<script setup>
// SiteUp: WhatsApp Cloud CSW + optional FEP notice above the reply box.
// CSW = free-form timer; FEP = "templates grátis" billing badge (does not extend CSW).
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessagingWindow } from 'dashboard/composables/useMessagingWindow';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  conversation: { type: Object, default: null },
  inbox: { type: Object, default: null },
  // Só liga a ação "Ver templates" quando a caixa realmente tem template
  // configurado — senão o botão abriria um modal vazio.
  canOpenTemplates: { type: Boolean, default: false },
});

const emit = defineEmits(['openTemplates']);

const { t } = useI18n();

const { hasWindow, isExpired, isAd, level, label, fepActive, fepLabel } =
  useMessagingWindow(() => props.conversation);

const isWhatsAppOfficial = computed(
  () => props.inbox?.channel_type === 'Channel::Whatsapp'
);

const show = computed(() => isWhatsAppOfficial.value && hasWindow.value);

// SiteUp V2 — caixa "escalonada" (decisão do Lucas 2026-08-09): o aviso é
// informação de urgência crescente, então a MESMA caixa muda de peso. Enquanto
// sobra tempo ela é um chip discreto no fio (linguagem do chip de data); a
// menos de 1h ou com a janela fechada vira barra com título, explicação e ação.
// Um banner gritando a 5h de folga é ruído que o atendente aprende a ignorar.
const isCritical = computed(() => isExpired.value || level.value === 'urgent');

// Cor semântica só no glifo, fundo sempre neutro — mesma regra das bolhas de
// chamada/mensagem não suportada (F1.5, PR #328).
const iconToneByLevel = {
  urgent: 'text-n-ruby-9',
  warning: 'text-n-amber-9',
  ok: 'text-n-teal-9',
  expired: 'text-n-ruby-9',
};

const chipTone = computed(
  () => iconToneByLevel[level.value] || iconToneByLevel.ok
);

// Lead de anúncio deixou de ser uma segunda caixa concorrente: vira sufixo de
// contexto aqui, e a marca de origem continua no selo do avatar.
const chipText = computed(() =>
  isAd.value
    ? t('CONVERSATION.MESSAGING_WINDOW.CHIP_OPEN_AD', {
        remaining: label.value,
      })
    : t('CONVERSATION.MESSAGING_WINDOW.CHIP_OPEN', { remaining: label.value })
);

const critical = computed(() => {
  if (isExpired.value) {
    return {
      icon: 'i-ri-lock-line',
      title: t('CONVERSATION.MESSAGING_WINDOW.CLOSED_TITLE'),
      // FEP só existe pra lead de anúncio, então é aqui que "veio de anúncio"
      // muda o que dá pra fazer: o template ainda sai de graça.
      subtitle: fepActive.value
        ? t('CONVERSATION.MESSAGING_WINDOW.CLOSED_SUB_FEP', {
            fep: fepLabel.value,
          })
        : t('CONVERSATION.MESSAGING_WINDOW.CLOSED_SUB'),
    };
  }

  return {
    icon: 'i-ri-time-line',
    title: t('CONVERSATION.MESSAGING_WINDOW.CLOSING_TITLE', {
      remaining: label.value,
    }),
    subtitle: t('CONVERSATION.MESSAGING_WINDOW.CLOSING_SUB'),
  };
});

// Sem botão no estado "apertando": o composer está logo abaixo, um "Responder"
// ali seria decoração. A ação só aparece quando ela leva a algum lugar.
const showTemplatesAction = computed(
  () => isExpired.value && props.canOpenTemplates
);

// Com a janela fechada o subtítulo já conta o FEP; a linha separada só faz
// sentido enquanto ainda dá pra responder livre.
const showFepRow = computed(() => fepActive.value && !isExpired.value);
</script>

<template>
  <div v-if="show" class="flex flex-col gap-1.5 mx-2 mb-2">
    <div
      v-if="isCritical"
      class="flex gap-2.5 items-center px-3 py-2.5 rounded-xl bg-n-alpha-2"
    >
      <span
        class="flex justify-center items-center rounded-[10px] size-7 shrink-0 bg-white/[0.06]"
      >
        <Icon class="size-3.5 text-n-ruby-9" :icon="critical.icon" />
      </span>
      <span class="flex flex-col flex-1 min-w-0">
        <span
          class="text-[13px] font-bold leading-tight tracking-tight font-display text-n-slate-12"
        >
          {{ critical.title }}
        </span>
        <span class="text-xs leading-tight text-n-slate-11">
          {{ critical.subtitle }}
        </span>
      </span>
      <button
        v-if="showTemplatesAction"
        type="button"
        class="inline-flex items-center px-3 h-7 text-xs font-bold rounded-full shrink-0 bg-n-slate-12 text-n-solid-1"
        @click="emit('openTemplates')"
      >
        {{ $t('CONVERSATION.MESSAGING_WINDOW.ACTION_TEMPLATES') }}
      </button>
    </div>

    <div
      v-else
      class="inline-flex gap-1.5 items-center self-center px-3 py-1 text-xs rounded-full bg-n-alpha-2"
    >
      <Icon class="size-3.5 shrink-0" :class="chipTone" icon="i-ri-time-line" />
      <span class="min-w-0 text-n-slate-11 truncate">{{ chipText }}</span>
    </div>

    <div
      v-if="showFepRow"
      class="inline-flex gap-1.5 items-center self-center px-3 py-1 text-xs rounded-full bg-n-alpha-2"
    >
      <Icon
        class="size-3.5 shrink-0 text-n-teal-9"
        icon="i-ri-discount-percent-line"
      />
      <span class="min-w-0 text-n-slate-11 truncate">{{ fepLabel }}</span>
    </div>
  </div>
</template>
